
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * ProgressCard
 *
 * @file ProgressCard.jsx
 * @author Mohamed yasar arabath M
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import useProgressCardStyle from './styles/ProgressCardStyle';
import CircularProgressBarWidget from '../common/CircularProgressBarWidget';
import { COMPLETED } from '../../common/constants';
import { BadgeTemplate } from '../tabular/components/Template';
import Utils from '../../common/utils';
import CustomCard from '../common/CustomCard';
import HtmlTooltip, { StatusTemplate } from '../common/HtmlTooltip';

const ProgressCard = ({
  title,
  segments,
  footerContent,
  progressSubLabel,
  comprehensionData,
  showBadge,
  tooltipSegments,
  isPercentage,
  enableDotIndicators
}) => {
  const classes = useProgressCardStyle();
  const [progressCardActive, setProgressCardActive] = useState(null);

  const completedCount = segments.reduce((acc, seg) => {
    let value = acc;
    if (Utils.localStringCompare(seg.type, COMPLETED)) {
      value += seg.value;
    }

    return value;
  }, 0);

  const tooltipContent = tooltipSegments?.length ? (
    <StatusTemplate segments={tooltipSegments} isPercentage={isPercentage} />
  ) : null;

  const onProgressCardMouseEnter = (segment) => {
    setProgressCardActive(segment.color);
  };

  const onProgressCardMouseLeave = () => {
    setProgressCardActive(null);
  };

  const renderBadgeTemplate = () => (
    <div className={classes.badgeWrapper}>
      <BadgeTemplate
        topics={null}
        status={comprehensionData.status}
        label={comprehensionData.comprehension}
        showIcon={comprehensionData.showIcon}
      />
    </div>
  );

  const renderBadge = () => {
    if (!comprehensionData || !showBadge) {
      return null;
    }

    return comprehensionData?.showTooltip ?
      <HtmlTooltip title={tooltipContent} placement="bottom" arrow>
        {renderBadgeTemplate(comprehensionData)}
      </HtmlTooltip>
      : renderBadgeTemplate(comprehensionData);
  };

  return (
    <CustomCard
      classes={{
        root: classes.progressCardContainer
      }}
    >
      <div
        className={`${classes.progressCardHeader} ${classes.badgeLabel}`}
      >
        <Typography className={classes.progressCardTitle}>{title}</Typography>

        {/* Right side comprehension badge with tooltip */}
        {renderBadge()}
      </div>
      <div className={classes.progressCardContent}>
        <CircularProgressBarWidget
          segments={segments}
          progressLabel={completedCount}
          progressSubLabel={progressSubLabel}
          onMouseEnter={onProgressCardMouseEnter}
          onMouseLeave={onProgressCardMouseLeave}
          activeColor={progressCardActive}
          unControlledTooltip
          enableDotIndicators={enableDotIndicators}
        />
        <div className={classes.progressCardBody}>
          <div className={classes.progressStatsSection}>
            <div className={classes.progressItemsList}>
              {segments.map((seg) => (
                <div
                  key={seg.label}
                  className={classes.progressItem}
                  onMouseEnter={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    onProgressCardMouseEnter(seg);
                  }}
                  onMouseLeave={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    onProgressCardMouseLeave();
                  }}
                >
                  <div className={classes.statusDotContainer}>
                    <div className={`${classes.dotBase} ${progressCardActive === seg.color ? classes.dotBaseHover : ''}`} style={{ backgroundColor: seg.color, color: seg.color }} />
                  </div>
                  <Typography className={classes.statusLabel}>{seg.label}:</Typography>
                  <Typography className={classes.percentageText}>{seg.value}</Typography>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {footerContent ?
        (
          <div className={classes.progressCardFooter}>
            {footerContent.map((item) => (
              <div key={item.label} className={classes.dateSection}>
                <Typography className={classes.dateLabel}>{item.label}</Typography>
                <Typography className={classes.dateLabel}>{item.value}</Typography>
              </div>
            ))}
          </div>
        ) : null}
    </CustomCard>
  );
};

ProgressCard.defaultProps = {
  footerContent: null,
  progressSubLabel: null,
  comprehensionData: null,
  showBadge: false,
  tooltipSegments: [],
  isPercentage: true,
  enableDotIndicators: false
};

ProgressCard.propTypes = {
  title: PropTypes.string.isRequired,
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      type: PropTypes.string
    })
  ).isRequired,
  footerContent: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  progressSubLabel: PropTypes.string,
  comprehensionData: PropTypes.shape({
    comprehension: PropTypes.string.isRequired,
    completedOnTime: PropTypes.number.isRequired,
    completedPastDue: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    showIcon: PropTypes.bool.isRequired,
    showTooltip: PropTypes.bool.isRequired
  }),
  showBadge: PropTypes.bool,
  tooltipSegments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired
    })
  ),
  isPercentage: PropTypes.bool,
  enableDotIndicators: PropTypes.bool
};

export default ProgressCard;

